import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CustomBreadcrumb from '../../common/CustomBreadcrumb';
import { BreadCrum } from '../../common';
import { ArrowLeft, EditIcon, FaqIcon, GalleryImage, UploadCsvIcon, VideoPlayer } from '../../assets/icon';
import Loader from '../Loder/loader';
import { getMaterialDetailsHandler } from '../../redux/action/getMaterialDetailsSlice';
import './ManageMaterial.css';
import PreviewModal from '../../common/PreviewModal';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';

const FileCard = ({ name, size, language, type, onClick }) => (
    <div className="material-file-card" onClick={onClick}>
        <div className="file-info-left">
            <div className="file-icon-box">
                {type === 'image' && (
                    <GalleryImage color='#162C4E' />
                )}
                {type === 'video' && (
                    <VideoPlayer color='#162C4E' />
                )}
                {type === 'pdf' && (
                    <UploadCsvIcon color="#162C4E" />
                )}
            </div>
            <div className="d-flex flex-column">
                <span className="file-name fs-14">{name}</span>
                <span className="file-size">
                    {size}
                    {language ? ` (${language})` : ""}
                </span>
            </div>
        </div>
        <div >
            <ArrowLeft />
        </div>
    </div>
);

const SectionHeader = ({ icon, title, count, type }) => (
    <div className="material-section-header fs-16">
        <div className="section-icon-wrapper">
            {icon}
        </div>
        <span>{title} ({count})</span>
    </div>
);

const ManageMaterial = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { canEditCampaigns } = useAdminPermissions();
    const isViewOnly = !canEditCampaigns;
    const { state } = useLocation();
    const getMaterialDetailsState = useSelector((reduxState) => reduxState?.getMaterialDetails);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    useEffect(() => {
        const materialId = state?.id ?? state?.materialId;
        const refBrandId = state?.refBrandId;
        if (!materialId || !refBrandId) return;

        dispatch(getMaterialDetailsHandler({ materialId, refBrandId }));
    }, [state?.id, state?.materialId, state?.refBrandId]);

    const materialData = getMaterialDetailsState?.data?.data ?? {};
    const effectiveData = Object.keys(materialData || {}).length ? materialData : (state ?? {});

    const productInfo = {
        name: effectiveData?.productName ?? '-',
        code: effectiveData?.productCode ?? '-',
    };

    const imagesData = Array.isArray(effectiveData?.images) ? effectiveData.images : [];
    const videosData = Array.isArray(effectiveData?.videos) ? effectiveData.videos : [];
    const pdfsData = Array.isArray(effectiveData?.pdfs) ? effectiveData.pdfs : [];
    const faqsData = Array.isArray(effectiveData?.faqs) ? effectiveData.faqs : [];

    const handlePreview = (file, type) => {
        setSelectedFile({
            fileUrl: file?.signedUrl || '',
            name: file?.fileName || '',
            size: file?.fileSize || '-',
            type
        });
        setIsPreviewOpen(true);
    };

    if (getMaterialDetailsState?.isLoading) {
        return <Loader />;
    }

    return (
        <div className="rightside">
            <div className="rightcontent">
                {/* Header and Breadcrumb */}
                <div className="material-header-container">
                    <p>
                        <strong>Materials</strong>
                    </p>
                    <CustomBreadcrumb
                        items={[
                            { text: BreadCrum.HOME},
                            { text: "AI Campaigns" },
                            { text: "Material List", onClick: () => navigate('/ai-campaigns/Material') },
                            { text: "View Material", active: true }
                        ]}
                    />
                </div>
                {getMaterialDetailsState?.error && (
                    <div className="mb-3 fs-14" style={{ color: "#d33b3b" }}>
                        {getMaterialDetailsState.error}
                    </div>
                )}

                {/* Product Information Card */}
                <div className="usercard mb-30">
                    <div className="campaignDetailsCard d-flex justify-content-between align-items-center">
                        <span className="campaigncardHeader">Product Information</span>
                    </div>
                    <hr className="m-0 manage-campaign-divider" />
                    <div className="campaigncardBody grid-1 w-100">
                        <div className="d-grid grid-3 gap-20 ">
                            <div className='productCardContain'>
                                <div className="fs-14 mb-1 manage-campaign-field-label">Product Name</div>
                                <div className="fw-bold fs-16 manage-campaign-field-value">{productInfo.name}</div>
                            </div>
                            <div className='productCardContain'>
                                <div className="small mb-1 manage-campaign-field-label">Product Code</div>
                                <div className="fw-bold manage-campaign-field-value">{productInfo.code}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Material Details Card */}
                <div className="usercard mb-30">
                    <div className="campaignDetailsCard d-flex justify-content-between align-items-center">
                        <span className="campaigncardHeader fs-20">Material</span>
                        {!isViewOnly && (
                        <div
                            className="manage-material-edit d-flex align-items-center gap-2"
                            onClick={() => navigate('/ai-campaigns/add-material', { state: { ...effectiveData, isEditMode: true } })}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    navigate('/ai-campaigns/add-material', { state: { ...effectiveData, isEditMode: true } });
                                }
                            }}
                        >
                            <EditIcon />
                            <span>Edit</span>
                        </div>
                        )}
                    </div>
                    <hr className="m-0 manage-campaign-divider" />
                    <div className="campaigncardBody grid-1 w-100">
                        {/* Images Section */}
                        <div className='d-flex flex-column gap-3'>
                            <SectionHeader
                                type="images"
                                title="Uploaded Images"
                                count={String(
                                    imagesData.length || Number(effectiveData?.imageCount ?? 0)
                                ).padStart(2, '0')}
                                icon={
                                    <GalleryImage />
                                }
                            />
                            <div className="material-file-grid">
                                {imagesData.map((file, index) => (
                                    <FileCard
                                        key={file?.id ?? index}
                                        name={file?.fileName ?? `Image ${index + 1}`}
                                        size={file?.fileSize ?? '-'}
                                        language={file?.language ?? ''}
                                        type="image"
                                        onClick={() => handlePreview(file, 'image')}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className='d-flex flex-column gap-3'>
                            {/* Videos Section */}
                            <SectionHeader
                                type="videos"
                                title="Uploaded Videos"
                                count={String(
                                    videosData.length || Number(effectiveData?.videoCount ?? 0)
                                ).padStart(2, '0')}
                                icon={
                                    <VideoPlayer />
                                }
                            />
                            <div className="material-file-grid">
                                {videosData.map((file, index) => (
                                    <FileCard
                                        key={file?.id ?? index}
                                        name={file?.fileName ?? `Video ${index + 1}`}
                                        size={file?.fileSize ?? '-'}
                                        language={file?.language ?? ''}
                                        type="video"
                                        onClick={() => handlePreview(file, 'video')}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className='d-flex flex-column gap-3'>
                            {/* PDFs Section */}
                            <SectionHeader
                                type="pdfs"
                                title="PDFs"
                                count={String(
                                    pdfsData.length || Number(effectiveData?.pdfCount ?? 0)
                                ).padStart(2, '0')}
                                icon={
                                    <UploadCsvIcon color={"#C98300"} />
                                }
                            />
                            <div className="material-file-grid">
                                {pdfsData.map((file, index) => (
                                    <FileCard
                                        key={file?.id ?? index}
                                        name={file?.fileName ?? `PDF ${index + 1}`}
                                        size={file?.fileSize ?? '-'}
                                        language={file?.language ?? ''}
                                        type="pdf"
                                        onClick={() => handlePreview(file, 'pdf')}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* FAQ Section */}
                        <div className='d-flex flex-column gap-3'>
                            <SectionHeader
                                type="faqs"
                                title="Frequently Asked Questions"
                                count={faqsData.length || Number(effectiveData?.faqCount ?? 0)}
                                icon={
                                    <FaqIcon />
                                }
                            />
                            <div className="faq-list">
                                {faqsData.map((faq, index) => (
                                    <div key={faq?.id ?? index} className="faq-item">
                                        <div className="faq-q-box fs-16">Q{index + 1}</div>
                                        <div className="faq-content">
                                            <div className="faq-question fs-16">{faq?.question ?? '-'}</div>
                                            <div className="faq-answer fs-16">
                                                {faq?.answer ?? '-'}
                                                {faq?.language ? ` (${faq.language})` : ''}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <PreviewModal
                open={isPreviewOpen}
                onCancel={() => setIsPreviewOpen(false)}
                file={selectedFile}
            />
        </div>
    );
};

export default ManageMaterial;
